import { capitalize, groupBy } from 'lodash/fp';
import { FC, useMemo } from 'react';

import ReactSelect from 'dashboard/components/ReactSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import {
  caseGetSteps,
  filterUsableObjects,
  UsableObjectConfig,
} from 'dashboard/lib/workflows/objects';
import {
  WorkflowActionMeta,
  WorkflowError,
  WorkflowFieldObjectKey,
} from 'dashboard/models/workflow';
import {
  WorkflowActionCreateCaseV1,
  WorkflowActionUpdateCaseStatusV1,
  WorkflowStep,
} from 'dashboard/models/workflow-version';
import Spacer from 'design-system/components/Spacer';

interface ParamsUpdateCaseStatusV1Props {
  caseTemplates: WorkflowActionMeta['case-templates'];
  editable: boolean;
  errors: WorkflowError[];
  usableObjects: UsableObjectConfig[];
  pathToAction: WorkflowStep[];
  params: WorkflowActionUpdateCaseStatusV1['params'];
  focusStep: (token: string) => void;
  onMenuClose: () => void;
  updateActionParams: (newParams: WorkflowActionUpdateCaseStatusV1['params']) => void;
}

const ParamsUpdateCaseStatusV1: FC<ParamsUpdateCaseStatusV1Props> = ({
  caseTemplates,
  editable,
  errors,
  usableObjects,
  pathToAction,
  params,
  focusStep,
  onMenuClose,
  updateActionParams,
}) => {
  const cases = useMemo(() => {
    return filterUsableObjects({
      usableObjects,
      options: {
        permittedObjects: [WorkflowFieldObjectKey.Case],
        excludeNonPersonaObjects: true,
      },
    });
  }, [usableObjects]);

  const selectCase = (kase: WorkflowActionUpdateCaseStatusV1['params']['case']) => {
    updateActionParams({
      ...params,
      case: kase,
    });
  };

  const possibleStates = useMemo(() => {
    const determinedCaseTemplate = caseGetSteps(pathToAction).find((c) => c.token === params.case);

    if (determinedCaseTemplate) {
      const caseToken = (determinedCaseTemplate.action as WorkflowActionCreateCaseV1).params[
        'case-template-token'
      ];
      const states = caseTemplates
        .find(({ token }) => token === caseToken)
        ?.states.filter((s) => !s['archived-at']);
      if (states) {
        return states.map((res) => ({ label: res.name, value: res.token }));
      }
    }

    /*
      Return all possible states grouped by name when case template is not determined e.g {'open', ['state_1', 'state_2']}
      When params.case is trigger or previously created case which didn't select case template yet
    */
    const allTemplatesStates = Object.values(caseTemplates)
      .flatMap((template) => template.states)
      .filter((s) => !s['archived-at']);
    const statesGroupByName = groupBy((x) => x.name.toLowerCase(), allTemplatesStates);

    return Object.entries(statesGroupByName).map(([name, states]) => ({
      label: capitalize(name),
      value: states.map((state) => state.token).join(','),
    }));
  }, [pathToAction, params.case, caseTemplates]);

  const selectStatus = (stateIds: string) => {
    updateActionParams({
      ...params,
      'state-ids': stateIds.split(','),
    });
  };

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionUpdateCaseStatusV1['params']>(errors);

  return (
    <>
      <Spacer height={2} />
      <ParamLabel labelText='Case' htmlFor='params-update-case-status__case-input' />
      <ReactSelect
        value={params.case}
        options={cases}
        onChange={selectCase}
        id='params-update-case-status__case'
        disabled={!editable}
        hasError={hasErrorFor('case')}
        onFocusOption={focusStep}
        onMenuClose={onMenuClose}
      />
      {renderErrorMessageFor('case')}
      <Spacer height={2} />
      <ParamLabel labelText='Status' htmlFor='params-update-case-status__status-input' />
      <ReactSelect
        value={params['state-ids'].join(',')}
        options={possibleStates}
        onChange={selectStatus}
        id='params-update-case-status__status'
        disabled={!editable}
        hasError={hasErrorFor('state-ids')}
      />
      {renderErrorMessageFor('state-ids')}
    </>
  );
};

export default ParamsUpdateCaseStatusV1;
